import { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CourseOption } from '../../types/course.response';
import { getCourseKey, getCourseCredit } from '../../utils/courseUtils';
import { autoAssignSections, classSectionIdsToNumbers } from '../../utils/scheduleUtils';
import { generateColorMap } from '../../utils/colorUtils';
import { getSemesterName } from '../../utils/semesterUtils';
import WeeklyScheduleGrid from '../../pages/schedule/WeeklyScheduleGrid';
import CourseCardForSelecting from '../../pages/schedule/CourseCard';
import Loading from '../loading/Loading';

interface SchedulePreviewModalProps {
  courseOption: CourseOption;
  semester: string;
  userCourses: CourseOption[];
  onConfirm: (classSectionIds: number[]) => void | Promise<void>;
  onCancel: () => void;
}

function sectionNumbersToIds(
  course: CourseOption,
  semester: string,
  sectionNumbers: string[],
): number[] {
  const numSet = new Set(sectionNumbers);
  return course.enrollGroups
    .filter(eg => eg.semester === semester)
    .flatMap(eg => eg.classSections)
    .filter(cs => numSet.has(cs.sectionNumber))
    .map(cs => cs.id);
}

export default function SchedulePreviewModal({
  courseOption,
  semester,
  userCourses,
  onConfirm,
  onCancel,
}: SchedulePreviewModalProps) {
  const [isPending, setIsPending] = useState(false);

  const existingCourses = useMemo(
    () => userCourses.filter(c => c.userState.semester === semester),
    [userCourses, semester],
  );

  const initialSectionIds = useMemo(
    () => autoAssignSections(courseOption, semester, userCourses),
    [courseOption, semester, userCourses],
  );

  const initialSectionNumbers = useMemo(
    () => classSectionIdsToNumbers(courseOption, semester, initialSectionIds),
    [courseOption, semester, initialSectionIds],
  );

  const [previewSectionNumbers, setPreviewSectionNumbers] = useState<string[]>(initialSectionNumbers);

  const previewCourse: CourseOption = useMemo(() => ({
    ...courseOption,
    userState: {
      ...courseOption.userState,
      status: 'PLANNED',
      semester,
      isScheduled: true,
      sectionNumbers: previewSectionNumbers,
      creditsReceived: getCourseCredit(courseOption),
    },
  }), [courseOption, semester, previewSectionNumbers]);

  const allCourses = useMemo(
    () => [...existingCourses, previewCourse],
    [existingCourses, previewCourse],
  );

  const colorMap = useMemo(
    () => generateColorMap(allCourses, getCourseKey),
    [allCourses],
  );

  const previewCourseKey = getCourseKey(courseOption);

  const handleSectionSelect = useCallback((_course: CourseOption, type: string, sectionNbr: string) => {
    setPreviewSectionNumbers(prev => {
      const sameTypeSectionNumbers = new Set<string>();
      for (const eg of courseOption.enrollGroups) {
        if (eg.semester !== semester) continue;
        for (const cs of eg.classSections) {
          if (cs.sectionType === type) {
            sameTypeSectionNumbers.add(cs.sectionNumber);
          }
        }
      }
      const filtered = prev.filter(sn => !sameTypeSectionNumbers.has(sn));
      return [...filtered, sectionNbr];
    });
  }, [courseOption, semester]);

  const handleConfirm = async () => {
    setIsPending(true);
    try {
      const ids = sectionNumbersToIds(courseOption, semester, previewSectionNumbers);
      await onConfirm(ids);
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const dateRange: [string, string] = ['', ''];

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1200px] max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Preview Schedule</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Adding <span className="font-medium text-gray-700">{courseOption.courseInfo.subject} {courseOption.courseInfo.number}</span> to {getSemesterName(semester)}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Schedule Grid */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
            <WeeklyScheduleGrid
              semester={semester}
              dateRange={dateRange}
              colorMap={colorMap}
              currentCourses={allCourses}
              highlightedCourseKey={previewCourseKey}
            />
          </div>

          {/* Right: Course Cards */}
          <div className="w-[320px] flex-shrink-0 border-l border-gray-200 overflow-y-auto p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{getSemesterName(semester)} Courses</h3>
            {allCourses.map(course => {
              const key = getCourseKey(course);
              const isPreview = key === previewCourseKey;
              return (
                <CourseCardForSelecting
                  key={key}
                  courseOption={isPreview ? previewCourse : course}
                  semester={semester}
                  color={colorMap[key] || '#6B7280'}
                  onDeleteCourse={() => {}}
                  onSectionSelect={isPreview ? handleSectionSelect : () => {}}
                  onToggleScheduled={() => {}}
                  isHighlighted={isPreview}
                  isDimmed={!isPreview}
                />
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isPending}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
          >
            {isPending && <Loading size="sm" className="brightness-200" />}
            {isPending ? 'Adding...' : 'Confirm & Add'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
